import { useApolloClient } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Delete } from "@mui/icons-material";
import { Button, MenuItem } from "@mui/material";
import { IconButton } from "@mui/material";
import { ReactElement, useMemo } from "react";
import { useForm, useFieldArray, FieldValues } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import {
  MetadataFieldTypes,
  MetadataEnumFieldUISchema,
} from "@stw/yup-validators";

import {
  MetadataEnumFieldFragment,
  UpdateMetadataEnumFieldMutation,
  UpdateMetadataEnumFieldMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { FieldForm } from "~frontend/components/campaignMetadata/FieldForm";
import {
  createEmptySelectValue,
  handlePostSubmit,
} from "~frontend/components/campaignMetadata/common";
import Select from "~frontend/components/forms/FormSelect";
import { RhfTextField } from "~frontend/components/forms/react-hook-form-components";
import {
  UPDATE_METADATA_ENUM_FIELD,
  GET_TENANT_METADATA_FIELDS,
} from "~frontend/graphql/campaignMetadata";
import useMutation from "~frontend/util/useMutation";

type DefaultValueSelect = "Yes" | "No";

type UpdateEnumFieldProps = {
  editField?: MetadataEnumFieldFragment;
  initialName: string;
  onTypeChange: (name: string, type: MetadataFieldTypes) => void;
  onCancelClick: () => void;
};

type FormInput = FieldValues & z.input<typeof MetadataEnumFieldUISchema>;
type FormOutput = FieldValues & z.infer<typeof MetadataEnumFieldUISchema>;

const EnumFieldForm = FieldForm<FormInput>;

export function EditEnumFieldForm({
  editField,
  onTypeChange,
  onCancelClick,
  initialName,
}: UpdateEnumFieldProps): ReactElement {
  const id = editField ? editField.id : undefined;

  const initialValues: FormInput = useMemo(() => {
    if (!editField) {
      return {
        name: initialName,
        required: false,
        defaultValue: null,
        values: [createEmptySelectValue()],
      };
    }
    const values = editField.configuration.values.map(v => {
      return {
        key: uuidv4(),
        value: v,
      };
    });
    const { id, name, required, defaultEnumValue } = editField;
    const defaultValue =
      values.find(v => v.value === defaultEnumValue)?.key || null;
    return {
      id,
      name,
      required,
      defaultValue,
      values,
    };
  }, [editField, initialName]);

  const client = useApolloClient();
  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    getValues,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    resolver: zodResolver(MetadataEnumFieldUISchema),
    values: initialValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "values",
  });
  const defaultValue = watch("defaultValue");

  const [updateField] = useMutation<
    UpdateMetadataEnumFieldMutation,
    UpdateMetadataEnumFieldMutationVariables
  >(UPDATE_METADATA_ENUM_FIELD, {
    refetchQueries: [GET_TENANT_METADATA_FIELDS],
    awaitRefetchQueries: true,
  });

  async function onSubmit(formData: FormOutput) {
    const { name, required, values, defaultValue: selectedDefault } = formData;
    const defaultValue =
      selectedDefault &&
      (values.find(v => v.key === selectedDefault)?.value as string);
    const { errors, data } = await updateField({
      variables: {
        input: {
          id,
          name,
          required,
          defaultValue,
          values: values.map(v => v.value),
        },
      },
    });

    handlePostSubmit({
      onSuccess: onCancelClick,
      setError,
      apolloClient: client,
      isNewField: !editField,
      data: data?.createOrUpdateCampaignMetadataEnumField,
      errors,
    });
  }

  function onFormCancelClick() {
    reset();
    onCancelClick();
  }

  function onDefaultValueChange(
    selectValueId: string,
    newDefault: DefaultValueSelect
  ) {
    const currentDefault = getValues("defaultValue");
    if (newDefault === "Yes") {
      setValue("defaultValue", selectValueId);
    } else {
      if (selectValueId === currentDefault) {
        setValue("defaultValue", null);
      }
    }
  }

  function addSelectOption() {
    append(createEmptySelectValue());
  }

  function removeSelectOption(index: number) {
    if (fields.length === 1) {
      setError("values.0.value", {
        message: "At least one option is required.",
      });
      return;
    }
    remove(index);
  }

  return (
    <EnumFieldForm
      editField={editField}
      onTypeChange={onTypeChange}
      getValues={getValues}
      watch={watch}
      currentFieldType={MetadataFieldTypes.ENUM}
      onCancelClick={onFormCancelClick}
      isSubmitting={isSubmitting}
      handleSubmit={handleSubmit(onSubmit)}
      control={control}
    >
      {fields.map((f, index) => {
        return (
          <FlexBox key={f.id} direction="column">
            <FlexBox direction="row" gap={2} sx={{ pl: 2 }}>
              <Select
                label="Default Value?"
                value={f.key === defaultValue ? "Yes" : "No"}
                onChange={e =>
                  onDefaultValueChange(
                    f.key,
                    e.target.value as DefaultValueSelect
                  )
                }
                sx={{ minWidth: "25%" }}
              >
                <MenuItem value="No" key="No">
                  No
                </MenuItem>
                <MenuItem value="Yes" key="Yes">
                  Yes
                </MenuItem>
              </Select>
              <RhfTextField
                control={control}
                name={`values.${index}.value`}
                label={`Single Select Option ${index + 1}`}
                style={{ flexGrow: 1 }}
                characterLimit={50}
              />
              <FlexBox alignItems={"center"}>
                <IconButton onClick={() => removeSelectOption(index)}>
                  <Delete />
                </IconButton>
              </FlexBox>
            </FlexBox>
          </FlexBox>
        );
      })}
      <Button
        variant="contained"
        color="info"
        sx={{ maxWidth: "40%" }}
        onClick={addSelectOption}
      >
        + ADD SELECT OPTION
      </Button>
    </EnumFieldForm>
  );
}
